import { useEffect, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import type {
  EslConfig,
  EslConfigRequest,
  EslTestConnectionResult,
} from '@/api/endpoints/esl'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

interface Props {
  config?: EslConfig
  onSave: (body: EslConfigRequest) => void
  onTest: (apiKey: string) => void
  onSyncNow: () => void
  saving?: boolean
  testing?: boolean
  testResult?: EslTestConnectionResult | null
  syncing?: boolean
}

export function RestaurantEslForm({
  config,
  onSave,
  onTest,
  onSyncNow,
  saving,
  testing,
  testResult,
  syncing,
}: Props) {
  const { t } = useTranslation('restaurants')
  const [enabled, setEnabled] = useState(false)
  const [shopNumber, setShopNumber] = useState('')
  const [apiKey, setApiKey] = useState('')

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled)
      setShopNumber(config.shopNumber ?? '')
      setApiKey(config.apiKey ?? '')
    }
  }, [config])

  const missingRequired =
    enabled && (shopNumber.trim() === '' || apiKey.trim() === '')

  const submit = () => {
    if (missingRequired) return
    onSave({
      enabled,
      shopNumber: shopNumber.trim() === '' ? null : shopNumber.trim(),
      apiKey: apiKey.trim() === '' ? null : apiKey.trim(),
    })
  }

  return (
    <div className="max-w-xl space-y-6">
      <div className="flex items-center gap-3">
        <Switch
          id="esl-enabled"
          checked={enabled}
          onCheckedChange={setEnabled}
        />
        <Label htmlFor="esl-enabled">{t('esl.enabled')}</Label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="esl-shop-number">{t('esl.shopNumber')}</Label>
        <Input
          id="esl-shop-number"
          value={shopNumber}
          onChange={(e) => setShopNumber(e.target.value)}
          placeholder={t('esl.shopNumberPlaceholder')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="esl-api-key">{t('esl.apiKey')}</Label>
        <Input
          id="esl-api-key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={t('esl.apiKeyPlaceholder')}
        />
      </div>

      {missingRequired && (
        <p className="text-destructive text-sm">
          {t('esl.requiredWhenEnabled')}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Button onClick={submit} disabled={saving || missingRequired}>
          {saving ? t('esl.saving') : t('esl.save')}
        </Button>
        <Button
          variant="outline"
          onClick={() => onTest(apiKey.trim())}
          disabled={testing || apiKey.trim() === ''}
        >
          {testing ? t('esl.testing') : t('esl.testConnection')}
        </Button>
        <Button variant="outline" onClick={onSyncNow} disabled={syncing}>
          {syncing ? t('esl.syncing') : t('esl.syncNow')}
        </Button>
      </div>

      {testResult && (
        <p
          className={
            testResult.ok ? 'text-sm font-medium' : 'text-destructive text-sm'
          }
        >
          {testResult.ok ? t('esl.testOk') : t('esl.testFailed')}
          {testResult.error ? `: ${testResult.error}` : ''}
        </p>
      )}

      <div className="text-muted-foreground space-y-1 border-t pt-4 text-sm">
        <div>
          {t('esl.status.lastSyncAt')}: {config?.lastSyncAt ?? '—'}
        </div>
        <div>
          {t('esl.status.lastStatus')}: {config?.lastSyncStatus ?? '—'}
        </div>
        {config?.lastSyncError ? (
          <div>
            {t('esl.status.lastError')}: {config.lastSyncError}
          </div>
        ) : null}
      </div>
    </div>
  )
}
